import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { userApi, fileApi, S3_BASE_URL } from './api';

/* ── Design Tokens ────────────────────────────────────── */
const BLUE = '#2563EB';
const BLUE_DARK = '#1E40AF';
const BLUE_LIGHT = '#3B82F6';

/* ── Tiny Icons ───────────────────────────────────────── */
const IconCamera = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </svg>
);

const IconUser = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const IconCheck = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

/* ── Pretty labels for preferences ────────────────────── */
const PREF_LABELS = {
    VEG: '🥗 Vegetarian', NON_VEG: '🍖 Non-Veg', NON_SMOKER: '🚭 Non-Smoker',
    NON_DRINKER: '🚫🍺 Non-Drinker', PET_LOVER: '🐾 Pet Lover', NO_PETS: '🚫🐾 No Pets',
    WORK_FROM_HOME: '🏠 WFH', EARLY_SLEEPER: '🌙 Early Sleeper',
    LATE_NIGHT_PERSON: '🦉 Night Owl', FITNESS_FREAK: '💪 Fitness', MUSIC_LOVER: '🎵 Music',
    CLEAN_AND_TIDY: '✨ Clean & Tidy'
};

/* ── Component ────────────────────────────────────────── */
const OnboardingScreen = () => {
    const { tokens, setIsRegisteredUser, refreshAccessToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [uploadingPic, setUploadingPic] = useState(false);
    const [formData, setFormData] = useState({
        name: '', gender: 'MALE', age: '', city: '',
        preferences: [], intent: 'FLATPGSEARCH', profilePicUrl: ''
    });

    /* ── Handlers ──────────────────────────────────────── */
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingPic(true);
        try {
            const { key } = await fileApi.uploadFile(file, tokens.access);
            setFormData({ ...formData, profilePicUrl: key });
        } catch (err) {
            console.error(err);
            alert("Failed to upload image: " + (err.message || ''));
        } finally {
            setUploadingPic(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.age || !formData.city) {
            alert("Please fill all required fields");
            return;
        }
        // The DPDP Act treats under-18s as children and requires verifiable
        // parental consent, which we cannot obtain. The API rejects these too —
        // this check only surfaces it before the round trip.
        const ageNum = parseInt(formData.age, 10);
        if (Number.isNaN(ageNum) || ageNum < 18 || ageNum > 120) {
            alert("You must be at least 18 years old to use FlatLoop.");
            return;
        }
        setLoading(true);
        try {
            await userApi.createEntity({
                name: formData.name,
                gender: formData.gender,
                age: parseInt(formData.age),
                city: formData.city,
                officeLocation: null,
                budgetMin: null,
                budgetMax: null,
                preferences: formData.preferences,
                intent: formData.intent,
                profilePicUrl: formData.profilePicUrl
            }, tokens.access);
            await refreshAccessToken();
            setIsRegisteredUser(true);
        } catch (error) {
            console.error("Failed onboarding:", error);
            alert("Failed to save profile details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const togglePref = (pref) =>
        setFormData({
            ...formData,
            preferences: formData.preferences.includes(pref)
                ? formData.preferences.filter(p => p !== pref)
                : [...formData.preferences, pref]
        });

    /* ── Shared Styles ─────────────────────────────────── */
    const inputStyle = {
        width: '100%', padding: '14px 16px', borderRadius: '14px',
        border: '1.5px solid #DDE3DC', fontSize: '15px', outline: 'none',
        background: '#F9FAFB', transition: 'border-color 0.2s, box-shadow 0.2s',
        boxSizing: 'border-box',
    };
    const labelStyle = {
        display: 'block', fontSize: '13px', fontWeight: '700',
        color: '#374151', marginBottom: '8px', letterSpacing: '0.2px',
    };

    /* ── Render ─────────────────────────────────────────── */
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', background: '#F3F4F6', overflowY: 'auto' }}>

            {/* =========== RESPONSIVE STYLES =========== */}
            <style>{`
                .ob-wrapper { max-width: 520px; margin: 0 auto; padding: 0 20px 60px; width: 100%; }
                .ob-form-card { background: white; border-radius: 28px; padding: 36px 32px; box-shadow: 0 12px 40px rgba(0,0,0,0.06); border: 1px solid #E5E9E3; }
                .ob-row { display: flex; gap: 14px; }
                .ob-avatar-ring { width: 100px; height: 100px; border-radius: 50%; border: 3px solid rgba(255,255,255,0.5); position: relative; overflow: hidden; background: rgba(255,255,255,0.15); backdrop-filter: blur(8px); cursor: pointer; }
                .ob-avatar-ring:hover { border-color: rgba(255,255,255,0.8); }
                .ob-avatar-ring img { width: 100%; height: 100%; object-fit: cover; }
                .ob-pref-grid { display: flex; flex-wrap: wrap; gap: 8px; }
                .ob-intent { display: flex; align-items: center; padding: 16px; border: 2px solid #DDE3DC; border-radius: 16px; cursor: pointer; background: white; transition: all 0.2s; gap: 12px; }
                .ob-intent:hover { border-color: #F97316; }
                .ob-intent.active { border-color: ${BLUE}; background: #EFF6FF; }
                .ob-submit { width: 100%; padding: 16px; border: none; border-radius: 16px; font-size: 16px; font-weight: 800; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; }
                .ob-submit:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(34,106,70,0.35); }
                .ob-submit:disabled { opacity: 0.7; cursor: not-allowed; }
                @media (max-width: 480px) {
                    .ob-form-card { padding: 24px 18px; border-radius: 20px; }
                    .ob-row { flex-direction: column; gap: 16px; }
                    .ob-wrapper { padding: 0 12px 40px; }
                }
                @keyframes obFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {/* =========== BLUE GRADIENT HEADER =========== */}
            <header style={{
                background: `linear-gradient(155deg, ${BLUE_DARK} 0%, ${BLUE} 55%, ${BLUE_LIGHT} 100%)`,
                padding: '48px 20px 64px', position: 'relative', overflow: 'hidden',
                textAlign: 'center', flexShrink: 0,
            }}>
                {/* Decorative orbs */}
                <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '320px', height: '320px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-50px', left: '-50px', width: '220px', height: '220px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                    {/* Logo */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '28px' }}>
                        <img src="/flatloop.png" alt="FlatLoop" style={{ height: '36px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                    </div>

                    {/* Avatar upload */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
                        <div className="ob-avatar-ring">
                            {formData.profilePicUrl ? (
                                <img src={`${S3_BASE_URL}${formData.profilePicUrl}`} alt="Profile" />
                            ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <IconUser />
                                </div>
                            )}
                            <input type="file" onChange={handleFileChange} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} accept="image/*" />
                            {/* Camera badge */}
                            <div style={{
                                position: 'absolute', bottom: '2px', right: '2px',
                                width: '28px', height: '28px', borderRadius: '50%',
                                background: 'white', color: BLUE, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}>
                                <IconCamera />
                            </div>
                        </div>
                        <span style={{ fontSize: '12px', color: '#F97316', marginTop: '10px', fontWeight: '600' }}>
                            {uploadingPic ? '⏳ Uploading...' : 'Tap to add photo'}
                        </span>
                    </div>

                    {/* Title */}
                    <h1 style={{ fontSize: 'clamp(22px, 5vw, 30px)', fontWeight: '800', color: 'white', margin: '0 0 6px', letterSpacing: '-0.5px' }}>
                        Welcome! Let's set you up
                    </h1>
                    <p style={{ color: '#D9E6DC', fontSize: '14px', margin: 0, lineHeight: 1.5, maxWidth: '380px', marginLeft: 'auto', marginRight: 'auto' }}>
                        Just the basics — you can always update your profile later.
                    </p>
                </div>
            </header>

            {/* =========== FORM BODY =========== */}
            <div className="ob-wrapper" style={{ marginTop: '-32px', position: 'relative', zIndex: 10 }}>
                <div className="ob-form-card" style={{ animation: 'obFadeUp 0.5s ease-out' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>

                        {/* ── Full Name ─────────────────────── */}
                        <div>
                            <label style={labelStyle}>Full Name *</label>
                            <input
                                type="text" placeholder="Enter your full name"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = BLUE; e.target.style.boxShadow = '0 0 0 3px rgba(34,106,70,0.1)'; }}
                                onBlur={e => { e.target.style.borderColor = '#DDE3DC'; e.target.style.boxShadow = 'none'; }}
                            />
                        </div>

                        {/* ── Age + Gender row ─────────────── */}
                        <div className="ob-row">
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Age *</label>
                                <input
                                    type="number" placeholder="e.g. 24"
                                    value={formData.age}
                                    onChange={e => setFormData({ ...formData, age: e.target.value })}
                                    style={inputStyle}
                                    onFocus={e => { e.target.style.borderColor = BLUE; e.target.style.boxShadow = '0 0 0 3px rgba(34,106,70,0.1)'; }}
                                    onBlur={e => { e.target.style.borderColor = '#DDE3DC'; e.target.style.boxShadow = 'none'; }}
                                />
                            </div>
                            <div style={{ flex: 1.5 }}>
                                <label style={labelStyle}>Gender *</label>
                                <select
                                    value={formData.gender}
                                    onChange={e => setFormData({ ...formData, gender: e.target.value })}
                                    style={{ ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
                                >
                                    <option value="MALE">Male</option>
                                    <option value="FEMALE">Female</option>
                                    <option value="ANY">Other / Prefer not to say</option>
                                </select>
                            </div>
                        </div>

                        {/* ── Current City ─────────────────── */}
                        <div>
                            <label style={labelStyle}>Current City *</label>
                            <input
                                type="text" placeholder="e.g. Bangalore, Mumbai"
                                value={formData.city}
                                onChange={e => setFormData({ ...formData, city: e.target.value })}
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = BLUE; e.target.style.boxShadow = '0 0 0 3px rgba(34,106,70,0.1)'; }}
                                onBlur={e => { e.target.style.borderColor = '#DDE3DC'; e.target.style.boxShadow = 'none'; }}
                            />
                        </div>

                        {/* ── Divider ──────────────────────── */}
                        <div style={{ borderTop: '1px solid #E5E9E3', margin: '4px 0' }} />

                        {/* ── What are you looking for? ────── */}
                        <div>
                            <label style={labelStyle}>What are you looking for? *</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {[
                                    { value: 'FLATPGSEARCH', label: 'Looking for a Flat / PG', icon: '🔍' },
                                    { value: 'FLATOWNER', label: 'I want to rent out my Flat', icon: '🏠' },
                                    { value: 'PGOWNER', label: 'I manage a PG / Hostel', icon: '🏨' },
                                ].map(opt => (
                                    <div
                                        key={opt.value}
                                        className={`ob-intent${formData.intent === opt.value ? ' active' : ''}`}
                                        onClick={() => setFormData({ ...formData, intent: opt.value })}
                                    >
                                        <span style={{ fontSize: '20px' }}>{opt.icon}</span>
                                        <div style={{
                                            width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                                            border: `2px solid ${formData.intent === opt.value ? BLUE : '#D1D5DB'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: formData.intent === opt.value ? BLUE : 'transparent',
                                            transition: 'all 0.2s',
                                        }}>
                                            {formData.intent === opt.value && <IconCheck />}
                                        </div>
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: formData.intent === opt.value ? '700' : '500',
                                            color: formData.intent === opt.value ? '#1E3A8A' : '#4B5563',
                                        }}>
                                            {opt.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Divider ──────────────────────── */}
                        <div style={{ borderTop: '1px solid #E5E9E3', margin: '4px 0' }} />

                        {/* ── Lifestyle Preferences ────────── */}
                        <div>
                            <label style={labelStyle}>Lifestyle Preferences <span style={{ fontWeight: '400', color: '#9CA3AF' }}>(optional)</span></label>
                            <div className="ob-pref-grid">
                                {Object.entries(PREF_LABELS).map(([key, label]) => {
                                    const sel = formData.preferences.includes(key);
                                    return (
                                        <div
                                            key={key} onClick={() => togglePref(key)}
                                            style={{
                                                padding: '7px 14px', borderRadius: '24px', fontSize: '12px',
                                                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                                border: `1.5px solid ${sel ? BLUE : '#DDE3DC'}`,
                                                background: sel ? '#EFF6FF' : '#F8FAFC',
                                                color: sel ? '#1E3A8A' : '#6B7280',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {label}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Submit ───────────────────────── */}
                        <button
                            className="ob-submit"
                            onClick={handleSubmit}
                            disabled={loading}
                            style={{
                                marginTop: '8px',
                                background: loading ? '#9CA3AF' : `linear-gradient(135deg, ${BLUE_DARK}, ${BLUE_LIGHT})`,
                                color: 'white',
                                boxShadow: loading ? 'none' : '0 8px 24px rgba(34,106,70,0.3)',
                            }}
                        >
                            {loading ? (
                                <>
                                    <div style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                    Saving…
                                </>
                            ) : (
                                'Complete Profile & Continue →'
                            )}
                        </button>

                        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9CA3AF', margin: '4px 0 0', lineHeight: 1.5 }}>
                            You can update budget, office location & more in <strong style={{ color: '#6B7280' }}>Profile Settings</strong> later.
                        </p>
                    </div>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default OnboardingScreen;
